import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { motion } from 'framer-motion';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { IoEyeOutline } from 'react-icons/io5';
import { useSearchParams } from 'react-router';
import { ArrowRight, ArrowLeft, SquarePen } from 'lucide-react';
import api from '../../utils/api';

function Employee_Attendance() {
  const [attendanceList, setAttendanceList] = useState([]);
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [recordsPerPage] = useState(10);
  const [apiLoading, setApiLoading] = useState(false);
  const { currentUser, loading } = useUser();
  const baseUrl = process.env.REACT_APP_API_URL;
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedDate, setSelectedDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  });


  const formatTime12Hour = (timeString) => {
    if (!timeString) return '-';
    const [hour, minute] = timeString.split(':');
    const date = new Date();
    date.setHours(hour, minute);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const convertDecimalToHoursMinutes = (decimalHours) => {
    if (!decimalHours || isNaN(decimalHours)) return '-';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const fetchAttendance = async (page = 1, size = 8) => {
    setApiLoading(true);
    try {
      const searchTerm = searchParams.get("search") || "";
      const response = await api.get(
        `/api/daily-attendance/daily-reports?page=${page - 1}&size=${size}&date=${selectedDate}&search=${searchTerm}`
      );

      const data = response.data;

      const mapped = data.content.map((record) => ({
        id: record.id,
        employeeName: record.employeeName,
        punchIn: record.punchInTime,
        punchOut: record.punchOutTime,
        status: record.status,
        totalHoursWorked: record.totalHoursWorked,
        date: record.date,
      }));

      setAttendanceList(mapped);
      setTotalPages(data.totalPages);
      setCurrentPage(data.number + 1);
    } catch (error) {
      console.error("Failed to fetch attendance:", error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance(currentPage, recordsPerPage);
  }, [currentPage, selectedDate]);

  useEffect(() => {
    setCurrentPage(1);
    fetchAttendance(1);
  }, [searchParams]);

  const handleEdit = (record) => {
    setSelectedRecord({ ...record }); // clone to avoid direct state mutation
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/api/daily-attendance/${selectedRecord.id}`, {
        punchInTime: selectedRecord.punchIn,
        punchOutTime: selectedRecord.punchOut,
        status: selectedRecord.status,
      });

      setShowEditModal(false);
      fetchAttendance(currentPage, recordsPerPage);
    } catch (error) {
      console.error("Update failed:", error);
      alert("Update failed. Please try again.");
    }
  };

  const changeDateBy = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split("T")[0]);
    setCurrentPage(1);
  };

  return (
    <div className="p-4 sm:p-6 w-full mx-auto space-y-6">
      <motion.div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2 className="text-2xl font-bold text-slate-800">Employee Daily Attendance</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeDateBy(-1)}
            className="px-3 py-2 bg-slate-800 rounded-full text-white hover:bg-slate-800 transition"
          >
            <ArrowLeft />
          </button>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-slate-300 rounded-full px-4 py-2 text-slate-700 shadow-sm hover:shadow-md transition"
          />

          <button
            onClick={() => changeDateBy(1)}
            className="px-3 py-2 bg-slate-800 rounded-full text-white hover:bg-slate-800 transition"
          >
            <ArrowRight />
          </button>
        </div>
      </motion.div>

      <motion.div className="bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-700">
            <thead className="bg-white border-b border-slate-200">
              <tr>
                {['Sl. No', 'Employee', 'Punch In', 'Punch Out', 'Total Hours', 'Status'].map((heading, idx) => (
                  <th key={idx} className="px-6 py-4 text-left font-semibold">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {apiLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                  </td>
                </tr>
              ) : attendanceList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-slate-500">No attendance records found.</td>
                </tr>
              ) : (
                attendanceList.map((record, index) => (
                  <tr key={record.id} className="hover:bg-slate-100/50 transition-all duration-200">
                    <td className="px-6 py-4">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                    <td className="px-6 py-4 font-medium">{record.employeeName}</td>
                     <td className="px-6 py-4">{formatTime12Hour(record.punchIn)}</td>
                     <td className="px-6 py-4">{formatTime12Hour(record.punchOut)}</td>
                     <td className="px-6 py-4">{convertDecimalToHoursMinutes(record.totalHoursWorked)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        record.status === "Present" ? "bg-green-100 text-green-700" :
                        record.status === "Absent" ? "bg-red-100 text-red-700" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>
                        {record.status}
                      </span>
                    </td>
                    {/* <td className="px-6 py-4 text-center">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleEdit(record)}
                        className="text-orange-600 hover:text-orange-800 "
                      >
                         <SquarePen className='text-sm' />
                      </motion.button>
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {attendanceList.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#334155',
                      borderColor: '#cbd5e1',
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#334155 !important',
                      color: '#fff',
                    },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {showEditModal && selectedRecord && (
  <div
    className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 flex items-center justify-center"
    onClick={(e) => {
      if (e.target === e.currentTarget) setShowEditModal(false);
    }}
  >
    <motion.div
      className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      {/* Close Button */}
      <button
        onClick={() => setShowEditModal(false)}
        className="absolute top-3 right-3 text-slate-400 hover:text-red-500 text-lg"
      >
        ✕
      </button>

      {/* Header */}
      <h2 className="text-xl font-semibold mb-5 text-center text-slate-800">
        Edit Attendance
      </h2>

      {/* Form Inputs */}
      <div className="space-y-3 text-slate-700 text-sm">
        <div>
          <label className="block mb-1 text-slate-500">Employee</label>
          <p className="font-medium">{selectedRecord.employeeName}</p>
        </div>

        <div>
          <label className="block mb-1 text-slate-500">Punch In</label>
          <input
            type="time"
            value={selectedRecord.punchIn || ''}
            onChange={(e) =>
              setSelectedRecord({ ...selectedRecord, punchIn: e.target.value })
            }
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring focus:ring-slate-300"
          />
        </div>

        <div>
          <label className="block mb-1 text-slate-500">Punch Out</label>
          <input
            type="time"
            value={selectedRecord.punchOut || ''}
            onChange={(e) =>
              setSelectedRecord({ ...selectedRecord, punchOut: e.target.value })
            }
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring focus:ring-slate-300"
          />
        </div>

        <div>
          <label className="block mb-1 text-slate-500">Status</label>
          <select
            value={selectedRecord.status}
            onChange={(e) =>
              setSelectedRecord({ ...selectedRecord, status: e.target.value })
            }
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring focus:ring-slate-300"
          >
            <option value="Present">Present</option>
            <option value="Absent">Absent</option>
            <option value="Half Day">Half Day</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => setShowEditModal(false)}
          className="px-4 py-1.5 rounded-full text-slate-600 border border-slate-300 hover:bg-slate-100 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleUpdate}
          className="px-4 py-1.5 rounded-full bg-green-600 text-white hover:bg-green-700 text-sm"
        >
          Save
        </button>
      </div>
    </motion.div>
  </div>
)}

    </div>
  );
}

export default Employee_Attendance;
